import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import {
    useGetQuoteItemsQuery,
    useUpdateQuoteItemDrawingMutation,
    useUpdateQuoteItemMutation,
} from '@/app/services/quoteItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useMutationErrorSnackbar } from '@/common/hooks'

const PartDetails = ({ drawing, quoteItemId }) => {
    const { t } = useTranslation()
    const notifyMutationError = useMutationErrorSnackbar()
    const { quoteId } = useParams()

    const organisationId = useSelector(selectOrganisationId)

    const { quoteItem } = useGetQuoteItemsQuery(
        {
            organisationId,
            quoteId,
        },
        {
            selectFromResult: ({ data }) => ({
                quoteItem: data?.find((item) => item.id === quoteItemId),
            }),
        }
    )

    const [updateQuoteItem] = useUpdateQuoteItemMutation()
    const [updateQuoteItemDrawing] = useUpdateQuoteItemDrawingMutation({ fixedCacheKey: 'updateQuoteItemDrawing' })

    const isPartFromLibrary = Boolean(quoteItem?.partLibraryEntryId)

    const [itemUnit, setItemUnit] = useState(quoteItem?.isImperial ? 'in' : 'mm')
    const [itemName, setItemName] = useState(quoteItem?.name || '')
    const [itemNotes, setItemNotes] = useState(quoteItem?.vendorNotes || '')

    const handleUpdateQuoteItem = async (quoteItem) => {
        try {
            await updateQuoteItem({
                organisationId,
                quoteId,
                quoteItemId: quoteItem?.id,
                quoteItem,
            }).unwrap()
        } catch (error) {
            notifyMutationError(error, t('Failed to update the $t(quote) item'))
        }
    }

    const debounceHandleUpdateQuoteItem = useDebouncedCallback(handleUpdateQuoteItem, 1000)

    const handleNameChange = async (event) => {
        const value = event?.target?.value
        setItemName(value)
        if (Boolean(value) && itemName !== value) {
            await debounceHandleUpdateQuoteItem({ ...quoteItem, name: value })
        }
    }

    const handleNotesChange = async (event) => {
        const value = event?.target?.value
        setItemNotes(value)
        if (Boolean(value) && itemNotes !== value) {
            await debounceHandleUpdateQuoteItem({ ...quoteItem, vendorNotes: value })
        }
    }

    const handleUnitChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemUnit !== value) {
            setItemUnit(value)
            const isImperial = value === 'in'

            await handleUpdateQuoteItem({ ...quoteItem, isImperial: isImperial })

            const updatedDrawing = { ...drawing, paths: Object.values(drawing.paths), isImperial: isImperial }

            await updateQuoteItemDrawing({
                organisationId,
                quoteId,
                quoteItemId,
                drawing: updatedDrawing,
            })
        }
    }

    return (
        <Box>
            {isPartFromLibrary ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'space-between',
                        margin: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography>{t('Part ID')}</Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <Typography>{quoteItem?.partId}</Typography>
                    </Box>
                </Box>
            ) : null}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Part Name')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <TextField
                            disabled={isPartFromLibrary}
                            size="small"
                            slotProps={{
                                htmlInput: {
                                    'data-testid': 'drawingdoctordrawer-partdetails-partname-input',
                                    sx: { color: 'white' },
                                },
                            }}
                            sx={{ width: '100%' }}
                            value={itemName}
                            variant="outlined"
                            onBlur={handleNameChange}
                            onChange={handleNameChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Units')}</Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <Select
                            color="primary"
                            disabled={isPartFromLibrary}
                            id="select-itemUnit"
                            inputProps={{
                                'data-testid': 'drawingdoctordrawer-partdetails-units-input',
                            }}
                            labelId="select-itemUnit-label"
                            size="small"
                            sx={{ width: '100%' }}
                            value={itemUnit}
                            variant="outlined"
                            onBlur={handleUnitChange}
                            onChange={handleUnitChange}
                        >
                            <MenuItem value="mm">{t('Millimeters')}</MenuItem>
                            <MenuItem value="in">{t('Inches')}</MenuItem>
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'top',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography>{t('Notes')}</Typography>
                </Box>
                <Box
                    sx={{
                        flex: 2,
                    }}
                >
                    <TextField
                        color="primary"
                        maxRows={4}
                        minRows={4}
                        slotProps={{
                            htmlInput: {
                                'data-testid': 'drawingdoctordrawer-partdetails-notes-input',
                            },
                        }}
                        sx={{ width: '100%' }}
                        value={itemNotes}
                        variant="outlined"
                        multiline
                        onBlur={handleNotesChange}
                        onChange={handleNotesChange}
                    />
                </Box>
            </Box>
        </Box>
    )
}

PartDetails.propTypes = {
    drawing: PropTypes.object,
    quoteItemId: PropTypes.string,
}

export default PartDetails
